import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { useDebts, usePros, useRefresh } from '../lib/data.ts';
import { initials, isoLocal, money, parseMoney, tone } from '../lib/format.ts';
import { BackHeader } from '../ui/Header.tsx';
import { Empty } from '../ui/Empty.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';

export function Pendencias() {
  const nav = useNav();
  const q = useDebts();
  const list = q.data ?? [];
  return (
    <div className="view-enter cp2"><BackHeader title="Pagar depois" />
      <div className="cp2-body">
        <p className="ac-intro">Quem deixou para pagar depois. A cota da parceira entra quando a cliente acerta.</p>
        {!list.length ? (q.data === undefined ? (q.isError ? <Failed error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />) : <Empty icon="card" title="Ninguém devendo">Quando uma cliente deixa para pagar depois, ela aparece aqui até acertar.</Empty>) : <div className="cx-total plain cx-rec">
          {list.map((d) => (
            <button key={d.id} className="close-line dv-row" onClick={() => nav.open({ kind: 'divida', clientId: d.id })}>
              <span>{d.name}<small>desde {new Date(d.since).toLocaleDateString('pt-BR')}</small></span><b>{money(d.debt)}</b></button>
          ))}
        </div>}
      </div>
    </div>
  );
}

interface Statement {
  balanceCents: number;
  lastPayout: { at: string; amountCents: number } | null;
  bonus: { enabled: boolean; goalCents: number; productionCents: number; earnedCents: number; launched: boolean; label: string };
  entries: Array<{ id: string; kind: string; amountCents: number; explanation: string; createdAt: string }>;
}
const KIND: Record<string, string> = { service_share: 'Cota', tip: 'Gorjeta', deposit_share: 'Sinal retido', deferred: 'Guardado (pagar depois)', deferred_release: 'Liberado', payout: 'Repasse pago', reversal: 'Estorno', adjustment: 'Ajuste ou bônus' };
const brDate = (iso: string) => isoLocal(iso).date.split('-').reverse().join('/');

// Parceiras e repasses: quanto falta repassar a cada uma, a meta do mês e o extrato.
export function Repasse() {
  const toast = useToast();
  const refresh = useRefresh();
  const prosQ = usePros();
  const pros = (prosQ.data ?? []).filter((p) => !p.isOwner);
  const qs = useQueries({ queries: pros.map((p) => ({ queryKey: ['statement', p.id], queryFn: () => api.get<Statement>(`/partners/${p.id}/statement`) })) });
  const [open, setOpen] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [extrato, setExtrato] = useState<string | null>(null);
  const loaded = qs.length > 0 && qs.every((q) => q.data);
  const total = qs.reduce((t, q) => t + Math.max(0, q.data?.balanceCents ?? 0), 0);
  return (
    <div className="view-enter cp2"><BackHeader title="Parceiras e repasses" />
      <div className="cp2-body rp">
        {prosQ.isError ? <Failed error={prosQ.error} retry={() => prosQ.refetch()} /> : !prosQ.data ? <Skeleton rows={3} /> : !pros.length ? (
          <Empty icon="team" title="Nenhuma parceira cadastrada">Cadastre as parceiras em Equipe para acompanhar a cota e os repasses de cada uma.</Empty>
        ) : <>
          <div className="rp-sum">
            <span>Total a repassar</span>
            <strong>{loaded ? money(total) : '…'}</strong>
            <small>Cotas e gorjetas lançadas na cobrança, menos os repasses já pagos.</small>
          </div>
          {pros.map((p, i) => {
            const q = qs[i];
            const st = q?.data;
            const goalPct = st?.bonus.enabled && st.bonus.goalCents > 0 ? Math.min(100, Math.round((st.bonus.productionCents / st.bonus.goalCents) * 100)) : 0;
            return (
              <section key={p.id} className="rp-card" aria-label={p.name}>
                <header className="rp-head">
                  <span className={`av t-${tone(p.id)}`}>{initials(p.name)}</span>
                  <span className="rp-who"><b>{p.name}</b><small>{st?.lastPayout ? `Último repasse ${money(st.lastPayout.amountCents)} em ${brDate(st.lastPayout.at)}` : 'Nenhum repasse pago ainda'}</small></span>
                </header>
                {q?.isError ? <Failed error={q.error} retry={() => q.refetch()} /> : !st ? <Skeleton rows={1} /> : <>
                  <div className="rp-bal">
                    <span>A repassar</span>
                    <strong className={st.balanceCents < 0 ? 'neg' : undefined}>{money(st.balanceCents)}</strong>
                  </div>
                  {st.bonus.enabled && (
                    <div className="rp-goal">
                      <div className="rp-goal-top"><span>Meta do mês</span><small>{money(st.bonus.productionCents)} de {money(st.bonus.goalCents)}</small></div>
                      <div className="rp-meter" role="progressbar" aria-valuenow={goalPct} aria-valuemin={0} aria-valuemax={100} aria-label="Produção do mês em relação à meta"><i style={{ width: `${goalPct}%` }} /></div>
                      {st.bonus.launched ? <p className="rp-note ok">Bônus do mês lançado no saldo.</p>
                        : st.bonus.earnedCents > 0 ? <button className="rp-btn soft" onClick={async () => {
                          try { await api.post(`/partners/${p.id}/bonus`, {}); await refresh(); await q?.refetch(); toast(`Bônus de ${money(st.bonus.earnedCents)} lançado no saldo de ${p.name}.`); } catch (e) { toast(msg(e), 'warn'); }
                        }}>Lançar bônus de {money(st.bonus.earnedCents)}</button>
                        : <p className="rp-note">Faltam {money(st.bonus.goalCents - st.bonus.productionCents)} para o bônus.</p>}
                    </div>
                  )}
                  {open === p.id ? (
                    <div className="rp-pay">
                      <label htmlFor={'rp' + p.id}>Valor pago (Pix, dinheiro ou transferência feitos fora do app)</label>
                      <div className="sv-price-in"><div className="money-in"><span>R$</span><input id={'rp' + p.id} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus /></div>
                        <button className="sv-apply" onClick={async () => {
                          const v = parseMoney(amount);
                          if (Number.isNaN(v) || v <= 0) return toast('Valor inválido.', 'warn');
                          try { await api.post(`/partners/${p.id}/payout`, { amountCents: v, note: 'Repasse pago' }); setOpen(null); await refresh(); await q?.refetch(); toast(`Repasse de ${money(v)} para ${p.name} registrado.`); } catch (e) { toast(msg(e), 'warn'); }
                        }}>Registrar</button></div>
                      <button className="rp-link" onClick={() => setOpen(null)}>Cancelar</button>
                    </div>
                  ) : st.balanceCents > 0 && (
                    <button className="rp-btn" onClick={() => { setOpen(p.id); setAmount((st.balanceCents / 100).toFixed(2).replace('.', ',')); }}>Pagar repasse</button>
                  )}
                  <button className="rp-link" aria-expanded={extrato === p.id} onClick={() => setExtrato(extrato === p.id ? null : p.id)}>{extrato === p.id ? 'Esconder extrato' : `Ver extrato (${st.entries.length})`}</button>
                  {extrato === p.id && <div className="rp-ext">
                    {!st.entries.length && <p className="rp-note">Nada lançado ainda.</p>}
                    {st.entries.slice(0, 60).map((e) => (
                      <div key={e.id} className="tl-row"><time>{brDate(e.createdAt).slice(0, 5)}</time><span>{KIND[e.kind] ?? e.kind}<small>{e.explanation}</small></span><b className={e.amountCents < 0 ? 'out' : 'in'}>{e.amountCents < 0 ? '− ' : ''}{money(Math.abs(e.amountCents))}</b></div>
                    ))}
                  </div>}
                </>}
              </section>
            );
          })}
        </>}
      </div>
    </div>
  );
}
